import { z } from "zod";
import {
	coordinateFields,
	validateCoordinatePair,
} from "@/core/lib/location/schema";
import { sitePathField, subDomainField } from "@/core/lib/slug";
import { youtubeLinkField } from "@/core/lib/youtube";

export enum nameResultsOrder {
	"Rate, Room, Activities" = "Rate, Room, Activities",
	"Room, Rate, Activities" = "Room, Rate, Activities",
	"Activities, Rate, Room" = "Activities, Rate, Room",
	nameResultOrder = "nameResultOrder",
}
export enum Status {
	Active = "Active",
	Paused = "Paused",
	Archived = "Archived",
}

export enum DateFormat {
	"MM/DD/YYYY" = "MM/DD/YYYY",
	"DD/MM/YYYY" = "DD/MM/YYYY",
	"YYYY-MM-DD" = "YYYY-MM-DD",
}

export enum Fonts {
	Roboto = "Roboto",
	"Open Sans" = "Open Sans",
	Lato = "Lato",
	Montserrat = "Montserrat",
	Oswald = "Oswald",
	Poppins = "Poppins",
	Raleway = "Raleway",
	"Noto Sans" = "Noto Sans",
	Merriweather = "Merriweather",
	"Nunito Sans" = "Nunito Sans",
	"PT Sans" = "PT Sans",
	"Source Sans Pro" = "Source Sans Pro",
	Ubuntu = "Ubuntu",
	"Playfair Display" = "Playfair Display",
	"Work Sans" = "Work Sans",
}

// Fuentes subidas por el usuario: `name` = font-family a usar en CSS,
// `file` = filename que devuelve el upload (para el @font-face del booking front).
export const customFontSchema = z.object({
	name: z.string().min(1),
	file: z.string().min(1),
});

export type CustomFont = z.infer<typeof customFontSchema>;

// Guest-facing vocabulary of the booking engine (Cart vs Reserve). The default
// lets an older property-service response without the field load and save as
// "marketplace", which is what every existing site shows today.
export const BOOKING_WORDINGS = ["marketplace", "hospitality"] as const;
export const bookingWordingSchema = z
	.enum(BOOKING_WORDINGS)
	.default("marketplace");
export type BookingWording = (typeof BOOKING_WORDINGS)[number];

export enum Timezone {
	"Etc/GMT+12" = "Etc/GMT+12",
	"Pacific/Midway" = "Pacific/Midway",
	"Pacific/Honolulu" = "Pacific/Honolulu",
	"Pacific/Marquesas" = "Pacific/Marquesas",
	"America/Anchorage" = "America/Anchorage",
	"America/Los_Angeles" = "America/Los_Angeles",
	"America/Denver" = "America/Denver",
	"America/Chicago" = "America/Chicago",
	"America/New_York" = "America/New_York",
	"America/Caracas" = "America/Caracas",
	"America/Halifax" = "America/Halifax",
	"America/St_Johns" = "America/St_Johns",
	"America/Sao_Paulo" = "America/Sao_Paulo",
	"America/Noronha" = "America/Noronha",
	"Atlantic/Azores" = "Atlantic/Azores",
	"Europe/London" = "Europe/London",
	"Europe/Paris" = "Europe/Paris",
	"Europe/Kaliningrad" = "Europe/Kaliningrad",
	"Europe/Moscow" = "Europe/Moscow",
	"Asia/Tehran" = "Asia/Tehran",
	"Asia/Dubai" = "Asia/Dubai",
	"Asia/Kabul" = "Asia/Kabul",
	"Asia/Karachi" = "Asia/Karachi",
	"Asia/Kolkata" = "Asia/Kolkata",
	"Asia/Kathmandu" = "Asia/Kathmandu",
	"Asia/Dhaka" = "Asia/Dhaka",
	"Asia/Yangon" = "Asia/Yangon",
	"Asia/Bangkok" = "Asia/Bangkok",
	"Asia/Shanghai" = "Asia/Shanghai",
	"Australia/Eucla" = "Australia/Eucla",
	"Asia/Tokyo" = "Asia/Tokyo",
	"Australia/Adelaide" = "Australia/Adelaide",
	"Australia/Sydney" = "Australia/Sydney",
	"Australia/Lord_Howe" = "Australia/Lord_Howe",
	"Pacific/Guadalcanal" = "Pacific/Guadalcanal",
	"Pacific/Norfolk" = "Pacific/Norfolk",
	"Pacific/Auckland" = "Pacific/Auckland",
	"Pacific/Chatham" = "Pacific/Chatham",
	"Pacific/Apia" = "Pacific/Apia",
	"Pacific/Kiritimati" = "Pacific/Kiritimati",
}

export enum Currency {
	USD = "USD",

	EUR = "EUR",
	JPY = "JPY",
	GBP = "GBP",
	AUD = "AUD",
	CAD = "CAD",
	CHF = "CHF",
	CNY = "CNY",
	HKD = "HKD",
	NZD = "NZD",
	AED = "AED",
	MXN = "MXN",
	BRL = "BRL",
	ARS = "ARS",
	COP = "COP",
	CLP = "CLP",
	PEN = "PEN",
}

export enum Language {
	EN = "EN",
	ES = "ES",
	"PT-BR" = "PT-BR",
}

export enum MetasearchProviders {
	None = "None",
	Mediagenius = "Mediagenius",
	Whip = "Whip",
}

export const MetasearchProvidersOptions = [
	{ label: "None", value: MetasearchProviders.None },
	{ label: "Mediagenius (D-Edge)", value: MetasearchProviders.Mediagenius },
	{ label: "Whip", value: MetasearchProviders.Whip },
];

export const PROPERTY_FORM_TABS = {
	siteDetails: "generalInfo",
	notifications: "generalInfo",
	formatting: "customization",
	customize: "customization",
	advanced: "advanced",
};

export const GROUP_UPDATE_FORM_TABS = {
	siteDetails: "generalInfo",
	customize: "customization",
	messages: "customization",
	advanced: "advanced",
};

export const PROPERTY_UPDATE_FORM_TABS = {
	siteDetails: "generalInfo",
	formatting: "generalInfo",
	notifications: "generalInfo",
	messages: "info",
	customize: "customization",
	advanced: "advanced",
};

export enum DnsStatus {
	Pending = "Pending",
	Completed = "Completed",
	Failed = "Failed",
}

export const dnsStatusColors = {
	Pending: "bg-yellow-300",
	Completed: "bg-success",
	Failed: "bg-destructive",
};

export const propertyFormSchema = z.object({
	siteDetails: z.object({
		siteName: z.string().min(1, { message: "val.required" }),
		location: z
			.object({
				country: z.string(),
				state: z.string(),
				address: z.string(),
				city: z.string(),
				zipCode: z.string(),
				isoCode: z.string().optional(),
				cityId: z.number().optional(),
				stateId: z.number().optional(),
				...coordinateFields,
			})
			.superRefine(validateCoordinatePair),
		phone: z.string().min(1, { message: "val.required" }),
		url: z.url(),
		domain: z.url().min(1, { message: "val.required" }),
		// Editable field: this is where the user types the subdomain, so it is
		// validated. The read-only copies of it (group site, manage site) keep a
		// loose schema: the value comes from the group and the user cannot fix it.
		subDomain: subDomainField,
		organizationId: z.string().length(16).optional(),
		preferredLanguage: z.enum(Language),
		groupId: z.string().optional(),
	}),
	formatting: z.object({
		dateFormat: z.enum(DateFormat),
		timezone: z.enum(Timezone),
		currency: z.enum(Currency),
	}),
	notifications: z.object({
		primaryEmail: z.email(),
		secondaryEmail: z.union([z.email(), z.string().max(0)]).optional(),
	}),
	customize: z.object({
		logo: z.string().min(1, { error: "val.required" }),
		favIcon: z.string().min(1, { error: "val.required" }),
		primaryColor: z.string(),
		secondaryColor: z.string(),
		accentColor: z.string(),
		backgroundColor: z.string(),
		primaryFont: z.string().min(1),
		secondaryFont: z.string().min(1),
		customFonts: z.array(customFontSchema).optional(),
		siteTitle: z.string().min(1, { error: "val.required" }),
		featuredImage: z.string().min(1, { error: "val.required" }),
		borderRadius: z.number().min(0).max(9).default(1),
	}),
});

export type PropertyFormValues = z.output<typeof propertyFormSchema>;
export type PropertyFormInputValues = z.input<typeof propertyFormSchema>;

// Exported so Collections can reuse the exact DNS row shape (same satellite
// table on the backend) instead of duplicating it.
export const dnsSchema = z.object({
	type: z.string(),
	name: z.string().nullable(),
	value: z.string().nullable(),
	ttl: z.number(),
	status: z.enum(DnsStatus),
});

export const propertiesUpdateFormSchema = z.object({
	siteDetails: z.object({
		location: z
			.object({
				country: z.string(),
				state: z.string(),
				address: z.string(),
				city: z.string(),
				zipCode: z.string(),
				isoCode: z.string().optional(),
				cityId: z.number().optional(),
				stateId: z.number().optional(),
				...coordinateFields,
			})
			.superRefine(validateCoordinatePair),
		id: z.string(),
		organizationId: z.string(),
		organizationName: z.string(),
		siteName: z.string().min(1).max(255),
		phone: z.string().min(1),
		url: z.url(),
		starRating: z.transform(Number).pipe(z.number()),
		earliestETA: z.string(),
		totalRoomCount: z.transform(Number).pipe(z.number().int()),
		latestETA: z.string(),
		bookingCutoffTime: z.string(),
		startingRoomRate: z.transform(Number).pipe(z.number()),
		roomLimitPerBooking: z.transform(Number).pipe(z.number().int()), //  0 = unlimited
		searchFacilities: z.array(z.string()),
		defaultAdults: z.transform(Number).pipe(z.number().int()),
		minimumNights: z.transform(Number).pipe(z.number().int()),
		roomRemainingThreshold: z.transform(Number).pipe(z.number().int()),
		nameResultsOrder: z.enum(nameResultsOrder),
		preferredLanguage: z.enum(Language),
		infantPrice: z.transform(Number).pipe(z.number().int()),
		status: z.enum(Status).optional(),
		domain: z.string().min(1, { error: "val.required" }).optional(),
		subDomain: z
			.string()
			.trim()
			.toLowerCase()
			.min(1, { error: "val.required" })
			.or(z.literal("")),
		domainPath: z.string().optional(),
		groupId: z.string().optional(),
		groupName: z.string().optional(),
	}),
	formatting: z.object({
		dateFormat: z.enum(DateFormat),
		timezone: z.enum(Timezone),
		currency: z.enum(Currency),
	}),
	notifications: z.object({
		primaryEmail: z.email(),
		secondaryEmail: z.email().optional().or(z.literal("")),
	}),
	customize: z.object({
		logo: z.string().min(1),
		favIcon: z.string().min(1),
		primaryColor: z.string(),
		secondaryColor: z.string(),
		accentColor: z.string(),
		backgroundColor: z.string(),
		primaryFont: z.string().min(1),
		secondaryFont: z.string().min(1),
		customFonts: z.array(customFontSchema).optional(),
		siteTitle: z.string(),
		featuredImage: z.string().min(1),
		cancelconfirmImage: z.string().min(1),
		borderRadius: z.number().min(0).max(9).default(1),
		activityOnly: z.boolean(),
		bookingWording: bookingWordingSchema,
	}),
	messages: z.object({
		bestRateGuaranteeMessage: z.string().max(255).optional(),
		termsAndConditions: z.string().max(1000).optional(),
		shortPropertyDescription: z.string().max(255).optional(),
		confirmationMessage: z.string().max(500).optional(),
		customerCancelationMessage: z.string().max(500).optional(),
	}),
	media: z.object({
		youtubeVideoId: youtubeLinkField,
		images: z.array(z.string()),
	}),
	advanced: z.object({
		dns: z.array(dnsSchema).optional(),
		dnsStatus: z.enum(DnsStatus).optional(),
		googleAnalytic4Code: z.string(),
		googleMapId: z.string(),
		googleTagManagerCode: z.string(),
		metasearchProvider: z.enum(MetasearchProviders).optional(),
		metasearchId: z
			.string()
			.min(1, { message: "val.required" })
			.max(15)
			.nullable(),
		conversionId: z.string().min(1, { message: "val.required" }).nullable(),
		conversionLabel: z.string().min(1, { message: "val.required" }).nullable(),
	}),
});

export type Property = z.input<typeof propertiesUpdateFormSchema>;
export type PropertiesUpdateFormInputValues = z.input<
	typeof propertiesUpdateFormSchema
>;

export const groupPropertySchema = propertyFormSchema.extend({});
export const editGroupPropertySchema = z.object({
	siteDetails: z.object({
		location: z
			.object({
				...coordinateFields,
				country: z.string(),
				state: z.string(),
				address: z.string(),
				city: z.string(),
				zipCode: z.string(),
				isoCode: z.string().optional(),
				cityId: z.number().optional(),
				stateId: z.number().optional(),
			})
			.superRefine(validateCoordinatePair),
		siteName: z.string().min(1),
		phone: z.string().min(1),
		url: z.url(),
		organizationId: z.string(),
		organizationName: z.string(),
		preferredLanguage: z.enum(Language),
		domain: z.string().min(1, { error: "val.required" }).optional(),
		subDomain: z
			.string()
			.trim()
			.toLowerCase()
			.min(1, { error: "val.required" })
			.optional()
			.or(z.literal("")),
	}),
	formatting: z.object({
		dateFormat: z.enum(DateFormat),
		timezone: z.enum(Timezone),
		currency: z.enum(Currency),
	}),
	notifications: z.object({
		primaryEmail: z.email(),
		secondaryEmail: z.email().optional().or(z.literal("")),
	}),
	customize: z.object({
		logo: z.string().min(1),
		favIcon: z.string().min(1),
		primaryColor: z.string(),
		secondaryColor: z.string(),
		accentColor: z.string(),
		backgroundColor: z.string(),
		primaryFont: z.string().min(1),
		secondaryFont: z.string().min(1),
		customFonts: z.array(customFontSchema).optional(),
		siteTitle: z.string(),
		featuredImage: z.string().min(1),
		borderRadius: z.number().min(0).max(9).default(1),
		bookingWording: bookingWordingSchema,
	}),
	messages: z
		.object({
			confirmationMessage: z.string().optional(),
			customerCancelationMessage: z.string().optional(),
		})
		.optional(),
	media: z.object({
		youtubeVideoId: youtubeLinkField,
		images: z.array(z.string()),
	}),
	advanced: z.object({
		dns: z.array(dnsSchema),
		dnsStatus: z.enum(DnsStatus),
		googleTagManagerCode: z.string(),
		googleMapId: z.string().optional(),
		googleAnalytic4Code: z.string().optional(),
	}),
});

export type Group = {
	siteDetails: {
		id: string;
		siteName: string;
		organizationId: string;
		organizationName: string;
		favicon: string;
		domain: string;
		subDomain: string;
	};
	sites: {
		id: string;
		siteName: string;
		favIcon: string;
		status: Status;
		location?: { address?: string; latitude?: number | null; longitude?: number | null } | null;
	}[];
	dnsStatus: DnsStatus;
};

export type GroupPropertyFormValues = z.input<typeof groupPropertySchema>;
export type GroupValues = z.input<typeof editGroupPropertySchema>;

export type EditGroupPropertyFormValues = z.input<
	typeof editGroupPropertySchema
>;

export const groupSiteFormSchema = propertyFormSchema
	.omit({
		siteDetails: true,
	})
	.extend({
		siteDetails: z.object({
			location: z
				.object({
					country: z.string(),
					state: z.string(),
					address: z.string(),
					city: z.string(),
					zipCode: z.string(),
					cityId: z.number(),
					stateId: z.number(),
					isoCode: z.string(),
					...coordinateFields,
				})
				.superRefine(validateCoordinatePair),
			domain: z.string().min(1, { error: "val.required" }).optional(),
			subDomain: z
				.string()
				.trim()
				.toLowerCase()
				.min(1, { error: "val.required" })
				.optional()
				.or(z.literal("")),
			siteName: z.string().min(1, { error: "val.required" }),
			phone: z.string().min(1, { error: "val.required" }),
			url: z.url(),
			// The `/property/{domainPath}` segment, the only URL field the user
			// fills in on this form. Pasted URLs used to go straight through.
			domainPath: sitePathField,
			groupId: z.string().length(16).optional(),
			preferredLanguage: z.enum(Language),
		}),
	});

export type GroupSiteFormValues = z.input<typeof groupSiteFormSchema>;
